/**
 * A2A Maths Service for PriMathsDaily
 * 
 * Service wrapper for A2A protocol communication with the A2AMathsAI backend.
 * Provides organized methods for Primary Mathematics learning features.
 */

import primaryMathsClient from '../a2aClient';

/**
 * A2A Maths Service Class
 * Wraps all A2A capabilities with clean, organized method names
 */
class A2AMathsService {
  constructor() {
    this.client = primaryMathsClient;
  }

  // ============================================================================
  // Primary Mathematics Methods
  // ============================================================================

  /**
   * Get primary mathematics grades (P1-P6)
   * @returns {Promise<Object>} Grades list with topics
   */
  async getPrimaryGrades() {
    try {
      const result = await this.client.executeTask('get_primary_grades', {});
      // Transform to match expected format
      if (result.success && result.data) {
        return result.data;
      }
      return result;
    } catch (error) {
      console.error('[A2A] getPrimaryGrades error:', error);
      throw error;
    }
  }

  /**
   * Get primary mathematics grade data (complete grade with all topics and questions)
   * @param {string} grade - Grade (P1-P6)
   * @returns {Promise<Object>} Complete grade data
   */
  async getPrimaryGradeData(grade) {
    try {
      const result = await this.client.executeTask('get_primary_grade_data', {
        grade: grade.toLowerCase()
      });
      
      // Transform A2A response to match OpenAPI format
      // Expected format: { success: true, data: { grade, topics: [...] } }
      if (result && result.data) {
        return result.data;
      }
      
      // If result is already in the correct format
      if (result && result.grade) {
        return result;
      }
      
      // Fallback: return as-is
      return result;
    } catch (error) {
      console.error('[A2A] getPrimaryGradeData error:', error);
      throw error;
    }
  }

  /**
   * Get primary mathematics topics by grade
   * @param {string} grade - Grade (p1-p6)
   * @returns {Promise<Object>} Topics for the grade
   */
  async getPrimaryTopics(grade) {
    try {
      const result = await this.client.executeTask('get_primary_topics', {
        grade: grade.toLowerCase()
      });
      if (result.success && result.data) {
        return result.data;
      }
      return result;
    } catch (error) {
      console.error('[A2A] getPrimaryTopics error:', error);
      throw error;
    }
  }

  /**
   * Get primary mathematics questions
   * @param {string} grade - Grade (p1-p6)
   * @param {Object} options - Query options
   * @param {string} options.topicId - Filter by topic ID
   * @param {number} options.page - Page number
   * @param {number} options.limit - Items per page
   * @returns {Promise<Object>} Questions with pagination
   */
  async getPrimaryQuestions(grade, options = {}) {
    try {
      const result = await this.client.executeTask('get_primary_questions', {
        grade: grade.toLowerCase(),
        topic_id: options.topicId || null,
        page: options.page || 1,
        limit: options.limit || 20
      });
      if (result.success && result.data) {
        return result.data;
      }
      return result;
    } catch (error) {
      console.error('[A2A] getPrimaryQuestions error:', error);
      throw error;
    }
  }

  /**
   * Generate drill questions using DeepSeek AI
   * @param {Object} originalQuestion - Original question object
   * @param {string} grade - Grade (P1-P6)
   * @param {Object} options - Generation options
   * @param {string} options.topicId - Topic ID
   * @param {number} options.numberOfDrills - Number of drills to generate
   * @param {Function} options.onProgress - Progress callback
   * @returns {Promise<Object>} Generated drill questions
   */
  async generatePrimaryDrillQuestions(originalQuestion, grade, options = {}) {
    try {
      // Extract question text from object if needed
      const questionText = typeof originalQuestion === 'string' 
        ? originalQuestion 
        : originalQuestion.question || originalQuestion.text || JSON.stringify(originalQuestion);

      if (options.onProgress) {
        options.onProgress({
          stage: 'preparing',
          message: '準備生成小學練習題...',
          tokensUsed: 0,
          estimatedTokens: 0,
          timeElapsed: 0
        });
      }

      // Prepare parameters - pass full question object if available for better prompt generation
      const params = {
        original_question: questionText,
        grade: grade.toLowerCase(),
        topic_id: options.topicId || null,
        number_of_drills: options.numberOfDrills || 5
      };
      
      // If originalQuestion is an object, pass it as question_data for detailed prompt
      if (typeof originalQuestion === 'object' && originalQuestion !== null) {
        params.question_data = originalQuestion;
      }
      
      // Add variation parameters to ensure different questions on regeneration
      if (options.variationSeed || options.requestVariation) {
        params.variation_seed = options.variationSeed || Date.now();
        params.request_variation = true;
        params.avoid_repetition = options.avoidRepetition !== false;
        // Add instruction to generate completely different questions
        params.regeneration_instruction = 'Please generate completely NEW and DIFFERENT practice questions. Do not repeat or reuse any questions from previous generations. Use different numbers, different problem structures, and different approaches while maintaining the same difficulty level and topic.';
      }
      
      const result = await this.client.executeTask('generate_primary_drill_questions', params, {
        timeout: 180000  // 180 seconds (3 minutes) for AI generation with complex prompts
      });

      // Transform A2A response to match expected format
      // Handle both formats: drillQuestions (new) or drill_questions (old)
      const drillQuestions = result.drillQuestions || 
                            result.drill_questions || 
                            (result.data && result.data.drillQuestions) ||
                            (result.data && result.data.drill_questions) ||
                            [];
      
      const transformed = {
        success: true,
        drillQuestions: drillQuestions,
        originalQuestion: result.original_question || result.originalQuestion || questionText,
        metadata: result.metadata || {}
      };
      
      if (options.onProgress) {
        options.onProgress({
          stage: 'complete',
          message: '練習題生成完成！',
          tokensUsed: result.meta?.usage?.total_tokens || result.usage?.total_tokens || 0,
          estimatedTokens: 0,
          timeElapsed: 0
        });
      }
      
      return transformed;
    } catch (error) {
      console.error('[A2A] generatePrimaryDrillQuestions error:', error);
      throw error;
    }
  }

  /**
   * Generate worksheet using DeepSeek AI
   * @param {string} grade - Grade (p1-p6)
   * @param {string} topicId - Topic ID
   * @param {Object} options - Generation options
   * @param {number} options.numQuestions - Number of questions
   * @param {string} options.difficulty - Difficulty level
   * @returns {Promise<Object>} Generated worksheet
   */
  async generatePrimaryWorksheet(grade, topicId, options = {}) {
    try {
      const result = await this.client.executeTask('generate_primary_worksheet', {
        grade: grade.toLowerCase(),
        topic_id: topicId,
        num_questions: options.numQuestions || 10,
        difficulty: options.difficulty || 'medium'
      }, {
        timeout: 60000  // 60 seconds for AI generation
      });
      if (result.success && result.data) {
        return result.data;
      }
      return result;
    } catch (error) {
      console.error('[A2A] generatePrimaryWorksheet error:', error);
      throw error;
    }
  }

  /**
   * Generate quiz using DeepSeek AI
   * @param {string} grade - Grade (p1-p6)
   * @param {Array} topics - Array of topic IDs
   * @param {Object} options - Generation options
   * @param {number} options.numQuestions - Number of questions
   * @param {number} options.timeLimit - Time limit in minutes
   * @returns {Promise<Object>} Generated quiz
   */
  async generatePrimaryQuiz(grade, topics, options = {}) {
    try {
      const result = await this.client.executeTask('generate_primary_quiz', {
        grade: grade.toLowerCase(),
        topics: topics || [],
        num_questions: options.numQuestions || 10,
        time_limit: options.timeLimit || 30
      }, {
        timeout: 60000  // 60 seconds for AI generation
      });
      if (result.success && result.data) {
        return result.data;
      }
      return result;
    } catch (error) {
      console.error('[A2A] generatePrimaryQuiz error:', error);
      throw error;
    }
  }

  /**
   * Generate flashcards using DeepSeek AI
   * @param {string} grade - Grade (p1-p6)
   * @param {string} topicId - Topic ID
   * @param {number} numCards - Number of cards to generate
   * @returns {Promise<Object>} Generated flashcards
   */
  async generatePrimaryFlashcards(grade, topicId, numCards = 10) {
    try {
      const result = await this.client.executeTask('generate_primary_flashcards', {
        grade: grade.toLowerCase(),
        topic_id: topicId,
        num_cards: numCards
      }, {
        timeout: 60000  // 60 seconds for AI generation
      });
      if (result.success && result.data) {
        return result.data;
      }
      return result;
    } catch (error) {
      console.error('[A2A] generatePrimaryFlashcards error:', error);
      throw error;
    }
  }

  /**
   * Generate exam paper using DeepSeek AI
   * @param {string} grade - Grade (p1-p6)
   * @param {Array} topics - Array of topic IDs
   * @param {Object} options - Generation options
   * @param {number} options.duration - Duration in minutes
   * @param {number} options.totalMarks - Total marks
   * @returns {Promise<Object>} Generated exam paper
   */
  async generatePrimaryExamPaper(grade, topics, options = {}) {
    try {
      const result = await this.client.executeTask('generate_primary_exam_paper', {
        grade: grade.toLowerCase(),
        topics: topics || [],
        duration: options.duration || 60,
        total_marks: options.totalMarks || 100
      }, {
        timeout: 60000  // 60 seconds for AI generation
      });
      if (result.success && result.data) {
        return result.data;
      }
      return result;
    } catch (error) {
      console.error('[A2A] generatePrimaryExamPaper error:', error);
      throw error;
    }
  }

  // ============================================================================
  // DeepSeek AI Service Methods
  // ============================================================================

  /**
   * DeepSeek chat for math problems
   * @param {string} message - User message
   * @param {Object} options - Chat options
   * @param {Object} options.context - Conversation context
   * @param {string} options.model - Model name (default: 'deepseek-chat')
   * @param {number} options.temperature - Temperature (default: 0.7)
   * @param {number} options.maxTokens - Max tokens (default: 2000)
   * @returns {Promise<Object>} AI response
   */
  async deepseekMathChat(message, options = {}) {
    try {
      const result = await this.client.executeTask('deepseek_math_chat', {
        message: message,
        context: options.context || {},
        model: options.model || 'deepseek-chat',
        temperature: options.temperature || 0.7,
        max_tokens: options.maxTokens || 2000
      }, {
        timeout: 60000  // 60 seconds for AI chat
      });
      
      // Transform A2A response to match OpenAPI format
      // Expected format: { success: true, data: { response: "...", usage: {...} } }
      if (result && result.data) {
        return {
          response: result.data.response || result.data.content || '',
          content: result.data.response || result.data.content || '',
          usage: result.data.usage || result.usage || {},
          model: result.data.model || result.model || 'deepseek-chat'
        };
      }
      
      // If response is already in expected format
      if (result && result.response) {
        return result;
      }
      
      // Fallback: return as-is
      return result;
    } catch (error) {
      console.error('[A2A] deepseekMathChat error:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const a2aMathsService = new A2AMathsService();
export default a2aMathsService;

